/**
 * Utility functions for consistent profile picture handling across the application
 */

/**
 * Get the profile picture URL using the consistent priority system
 * @param {Object} userProfile - User profile data from Firestore
 * @param {Object} user - Firebase Auth user object
 * @returns {string|null} Profile picture URL or null if none available
 */
export const getProfilePictureUrl = (userProfile, user) => {
  // Priority order:
  // 1. Custom uploaded profile picture from Firestore (userProfile.profilePicture)
  // 2. Profile picture from basicInfo in Firestore (userProfile.basicInfo?.photoURL)
  // 3. Firebase Auth profile picture (user.photoURL)
  return userProfile?.profilePicture || 
         userProfile?.basicInfo?.photoURL || 
         user?.photoURL || 
         null;
};

/**
 * Get initials from a display name or email for fallback avatar
 * @param {string} displayName - User's display name
 * @param {string} email - User's email (fallback)
 * @returns {string} User initials
 */
export const getInitials = (displayName, email) => {
  if (displayName) {
    const names = displayName.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return displayName[0].toUpperCase();
  }
  
  if (email) {
    return email[0].toUpperCase();
  }
  
  return '?';
};

/**
 * Get the current profile picture URL for a specific user
 * @param {string} userId - User ID to fetch profile picture for
 * @returns {Promise<string|null>} Current profile picture URL or null if none available
 */
export const getCurrentProfilePictureUrl = async (userId) => {
  try {
    const { getUserProfile } = await import('../services/firebase/user-profile');
    const result = await getUserProfile(userId);
    
    if (result.success && result.data) {
      const userProfile = result.data;
      // Use same priority system as getProfilePictureUrl
      return userProfile.profilePicture || 
             userProfile.basicInfo?.photoURL || 
             userProfile.photoURL || 
             null;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching current profile picture:', error);
    return null;
  }
};

/**
 * Create a profile picture component props object with consistent priority
 * @param {Object} userProfile - User profile data from Firestore
 * @param {Object} user - Firebase Auth user object
 * @param {string} altText - Alt text for the image
 * @returns {Object} Props object for profile picture component
 */
export const getProfilePictureProps = (userProfile, user, altText) => {
  const profilePictureUrl = getProfilePictureUrl(userProfile, user);
  
  return {
    src: profilePictureUrl,
    alt: altText || user?.displayName || user?.email || 'User',
    hasImage: !!profilePictureUrl,
    initials: getInitials(user?.displayName, user?.email)
  };
};